import { sessionStorage } from 'dibs-browser-storage';

import { isJsonObject } from '../helpers/isJsonObject.ts';
import {
    SUPPORT_CHAT_TYPES,
    SUPPORT_CHAT_TOUCHPOINTS,
    SELLER_SUPPORT_CHAT_TOUCHPOINTS,
} from '../constants.ts';
import { SESSION_STORAGE_KEY, DEFAULT_SESSION_STORAGE } from './constants.ts';

import { type StorageKey, type SupportChatStorage, type YesNoValue } from './types.ts';
import {
    type SupportChatType,
    type SupportChatTouchpoint,
    type SettablePreChatFields,
} from '../types.ts';

function stringOrUndefined(value: unknown): string | undefined {
    return typeof value === 'string' ? value : undefined;
}

function stringOrNull(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
}

const getYesNoValue = (value: unknown): YesNoValue => {
    return value === 'yes' ? 'yes' : 'no';
};

function getDefaultSessionStorage(): SupportChatStorage {
    return {
        ...DEFAULT_SESSION_STORAGE,
        preChatFields: { ...DEFAULT_SESSION_STORAGE.preChatFields },
    };
}

function getSessionStorage(): SupportChatStorage {
    const storage = sessionStorage.getItem(SESSION_STORAGE_KEY);

    if (!isJsonObject(storage)) {
        return getDefaultSessionStorage();
    }

    const storageActiveChat = storage.activeChat as SupportChatType | null;
    const isChatActive = storageActiveChat && SUPPORT_CHAT_TYPES.includes(storageActiveChat);
    const storageTouchpoint = storage.touchpoint as SupportChatTouchpoint | null;
    const touchpoint =
        isChatActive && storageTouchpoint && SUPPORT_CHAT_TOUCHPOINTS.includes(storageTouchpoint)
            ? storageTouchpoint
            : null;

    if (!touchpoint) {
        return getDefaultSessionStorage();
    }

    const isSellerChat = SELLER_SUPPORT_CHAT_TOUCHPOINTS.includes(touchpoint);
    const { pageType, topic, orderId, itemId } = isJsonObject(storage.preChatFields)
        ? (storage.preChatFields as SettablePreChatFields)
        : {};

    const isWindowVisible = getYesNoValue(storage.isWindowVisible);

    return {
        activeChat: storageActiveChat,
        touchpoint,
        isButtonVisible: isWindowVisible ? 'no' : getYesNoValue(storage.isButtonVisible),
        isWindowVisible,
        hasUnreadMessage: getYesNoValue(storage.hasUnreadMessage),
        showChatButtonOnLoad: getYesNoValue(storage.showChatButtonOnLoad),
        preChatFields: {
            pageType: stringOrUndefined(pageType) || 'support chat',
            topic:
                stringOrUndefined(topic) ||
                (isSellerChat ? 'sellingGeneralQuestions' : 'itemQuestions'),
            orderId: stringOrUndefined(orderId),
            itemId: stringOrUndefined(itemId),
        },
        salesforceJwt:
            storageActiveChat === 'SALESFORCE' ? stringOrNull(storage.salesforceJwt) : null,
    };
}

function getItemInSessionStorage<T extends StorageKey>(key: T): SupportChatStorage[T] | null {
    const storage = getSessionStorage();
    return storage[key] || null;
}

function setItemInSessionStorage<T extends StorageKey>(key: T, value: SupportChatStorage[T]): void {
    const rawStorage = sessionStorage.getItem(SESSION_STORAGE_KEY);
    const storage = isJsonObject(rawStorage) ? { ...rawStorage } : {};
    (storage as SupportChatStorage)[key] = value;
    sessionStorage.setItem(SESSION_STORAGE_KEY, storage);
}

function resetSessionStorage(): void {
    sessionStorage.setItem(SESSION_STORAGE_KEY, DEFAULT_SESSION_STORAGE);
}

export {
    getSessionStorage,
    getItemInSessionStorage,
    setItemInSessionStorage,
    resetSessionStorage,
    stringOrUndefined,
};
